import React, { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { SxProps, TextFieldProps, Theme } from '@mui/material';

import { agentApi, llmModelApi } from '../../services/api';
import type { AgentDetail, AgentListItem, LlmModel } from '../../types';

const DEFAULT_PAGE_SIZE = 25;

const useDebouncedValue = (value: string, delayMs = 250) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timeout);
  }, [delayMs, value]);

  return debounced;
};

const uniqueById = <T extends { id: number }>(items: T[]) => {
  const seen = new Set<number>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

const shouldLoadNextPage = (event: React.UIEvent<HTMLElement>) => {
  const listboxNode = event.currentTarget;
  const remaining =
    listboxNode.scrollHeight - listboxNode.scrollTop - listboxNode.clientHeight;
  return remaining < 80;
};

export interface LlmModelAutocompleteProps {
  value?: number | null | '';
  onChange: (modelId: number | null) => void;
  label?: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  filters?: {
    provider?: number;
    is_active?: boolean;
    audio_input_supported?: boolean;
    direct_audio_input_supported?: boolean;
    transcription_supported?: boolean;
    audio_output_supported?: boolean;
    speech_synthesis_supported?: boolean;
  };
  pageSize?: number;
  sx?: SxProps<Theme>;
  textFieldProps?: Omit<TextFieldProps, 'value' | 'onChange' | 'label'>;
}

export const LlmModelAutocomplete: React.FC<LlmModelAutocompleteProps> = ({
  value,
  onChange,
  label = 'LLM model',
  placeholder = 'Search models...',
  helperText,
  disabled = false,
  required = false,
  size = 'small',
  fullWidth = true,
  filters,
  pageSize = DEFAULT_PAGE_SIZE,
  sx,
  textFieldProps,
}) => {
  const selectedId = typeof value === 'number' && value > 0 ? value : null;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim());

  const modelsQuery = useInfiniteQuery({
    queryKey: ['llm-model-selector', filters || {}, debouncedSearch, pageSize],
    queryFn: ({ pageParam }) =>
      llmModelApi.list({
        ...(filters || {}),
        page: pageParam,
        page_size: pageSize,
        search: debouncedSearch || undefined,
      }),
    enabled: open && !disabled,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => (lastPage.next ? pages.length + 1 : undefined),
  });

  const loadedModels = useMemo(
    () => modelsQuery.data?.pages.flatMap((page) => page.results || []) || [],
    [modelsQuery.data]
  );

  const selectedModelInList =
    selectedId != null ? loadedModels.find((model) => model.id === selectedId) : null;

  const selectedModelQuery = useQuery({
    queryKey: ['llm-model-selector-selected', selectedId],
    queryFn: () => llmModelApi.get(selectedId as number),
    enabled: selectedId != null && !selectedModelInList,
    staleTime: 5 * 60 * 1000,
  });

  const options = useMemo(
    () => uniqueById([selectedModelQuery.data, ...loadedModels].filter(Boolean) as LlmModel[]),
    [loadedModels, selectedModelQuery.data]
  );

  const selectedModel =
    selectedId != null ? options.find((model) => model.id === selectedId) || null : null;
  const loading =
    modelsQuery.isLoading || modelsQuery.isFetchingNextPage || selectedModelQuery.isFetching;

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        setSearch('');
      }}
      options={options}
      value={selectedModel}
      loading={loading}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      sx={sx}
      filterOptions={(items) => items}
      noOptionsText={debouncedSearch ? 'No matching models' : 'No models available'}
      getOptionLabel={(option) =>
        option ? `${option.name}${option.provider_name ? ` (${option.provider_name})` : ''}` : ''
      }
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      onInputChange={(_, nextInput, reason) => {
        if (reason === 'input') setSearch(nextInput);
        if (reason === 'clear') setSearch('');
      }}
      onChange={(_, option) => onChange(option?.id ?? null)}
      disableClearable={required}
      ListboxProps={{
        style: { maxHeight: 320 },
        onScroll: (event) => {
          if (
            shouldLoadNextPage(event) &&
            modelsQuery.hasNextPage &&
            !modelsQuery.isFetchingNextPage
          ) {
            modelsQuery.fetchNextPage();
          }
        },
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {option.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {option.provider_name || 'Unknown provider'} - {option.model}
            </Typography>
          </Stack>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldProps}
          label={label}
          placeholder={placeholder}
          required={required}
          helperText={helperText ?? textFieldProps?.helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export interface AgentSelectorOption {
  id: number;
  name: string;
  description?: string | null;
  agent_type?: string;
  model_name?: string | null;
  is_active?: boolean;
  created_at?: string;
  tool_count?: number;
  capabilities?: string[];
}

const toAgentOption = (agent: AgentListItem | AgentDetail | AgentSelectorOption): AgentSelectorOption => ({
  id: agent.id,
  name: agent.name,
  description: agent.description,
  agent_type: agent.agent_type,
  model_name:
    'model_name' in agent && agent.model_name
      ? agent.model_name
      : 'model' in agent && agent.model
        ? agent.model.name
        : null,
  is_active: agent.is_active,
  created_at: agent.created_at,
  tool_count: agent.tool_count,
  capabilities: agent.capabilities,
});

export interface AgentAutocompleteProps {
  value?: number | null | '';
  onChange: (agentId: number | null) => void;
  onOptionChange?: (agent: AgentSelectorOption | null) => void;
  label?: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  filters?: {
    is_active?: boolean;
    is_template?: boolean;
    agent_type?: string;
  };
  pageSize?: number;
  sx?: SxProps<Theme>;
  startAdornment?: React.ReactNode;
  initialOption?: AgentSelectorOption | null;
  textFieldProps?: Omit<TextFieldProps, 'value' | 'onChange' | 'label'>;
}

export const AgentAutocomplete: React.FC<AgentAutocompleteProps> = ({
  value,
  onChange,
  onOptionChange,
  label = 'Agent',
  placeholder = 'Search agents...',
  helperText,
  disabled = false,
  required = false,
  size = 'small',
  fullWidth = true,
  filters,
  pageSize = DEFAULT_PAGE_SIZE,
  sx,
  startAdornment,
  initialOption = null,
  textFieldProps,
}) => {
  const selectedId = typeof value === 'number' && value > 0 ? value : null;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim());

  const agentsQuery = useInfiniteQuery({
    queryKey: ['agent-selector', filters || {}, debouncedSearch, pageSize],
    queryFn: ({ pageParam }) =>
      agentApi.list(pageParam, pageSize, {
        ...(filters || {}),
        search: debouncedSearch || undefined,
      }),
    enabled: open && !disabled,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => (lastPage.next ? pages.length + 1 : undefined),
  });

  const loadedAgents = useMemo(
    () =>
      agentsQuery.data?.pages.flatMap((page) =>
        (page.results || []).map((agent) => toAgentOption(agent))
      ) || [],
    [agentsQuery.data]
  );

  const selectedAgentInList =
    selectedId != null ? loadedAgents.find((agent) => agent.id === selectedId) : null;

  const selectedAgentQuery = useQuery({
    queryKey: ['agent-selector-selected', selectedId],
    queryFn: async () => toAgentOption(await agentApi.get(selectedId as number)),
    enabled: selectedId != null && !selectedAgentInList && !initialOption,
    staleTime: 5 * 60 * 1000,
  });

  const options = useMemo(
    () =>
      uniqueById(
        [initialOption, selectedAgentQuery.data, ...loadedAgents].filter(
          Boolean
        ) as AgentSelectorOption[]
      ),
    [initialOption, loadedAgents, selectedAgentQuery.data]
  );

  const selectedAgent =
    selectedId != null ? options.find((agent) => agent.id === selectedId) || null : null;
  const loading =
    agentsQuery.isLoading || agentsQuery.isFetchingNextPage || selectedAgentQuery.isFetching;

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        setSearch('');
      }}
      options={options}
      value={selectedAgent}
      loading={loading}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      sx={sx}
      filterOptions={(items) => items}
      noOptionsText={debouncedSearch ? 'No matching agents' : 'No agents available'}
      getOptionLabel={(option) => option?.name || ''}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      onInputChange={(_, nextInput, reason) => {
        if (reason === 'input') setSearch(nextInput);
        if (reason === 'clear') setSearch('');
      }}
      onChange={(_, option) => {
        onChange(option?.id ?? null);
        onOptionChange?.(option ?? null);
      }}
      disableClearable={required}
      ListboxProps={{
        style: { maxHeight: 320 },
        onScroll: (event) => {
          if (
            shouldLoadNextPage(event) &&
            agentsQuery.hasNextPage &&
            !agentsQuery.isFetchingNextPage
          ) {
            agentsQuery.fetchNextPage();
          }
        },
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Stack spacing={0.4} sx={{ minWidth: 0, width: '100%' }}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                {option.name}
              </Typography>
              {option.agent_type ? (
                <Chip size="small" variant="outlined" label={option.agent_type} />
              ) : null}
            </Stack>
            <Typography variant="caption" color="text.secondary" noWrap>
              {option.model_name || 'No model'}{option.description ? ` - ${option.description}` : ''}
            </Typography>
          </Stack>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldProps}
          label={label}
          placeholder={placeholder}
          required={required}
          helperText={helperText ?? textFieldProps?.helperText}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                {startAdornment}
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
